import { useState, useEffect } from "react";
import type { HTMLInputTypeAttribute } from "react";
import { ChevronDownIcon } from "lucide-react";
import DateTimePicker from "../../components/DateTimePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  getFilingDetails,
  getStampVendors,
  updateStampPaperDetailsToVendor,
  getStampPaperPreview,
  sendStampPaperWhatsApp,
  type FilingDetailsResponse,
  type StampVendorOption,
  type UpdateStampPaperDetailsToVendorPayload,
} from "../../api/discussion";
import { normalizeApiDateToIsoForForm } from "../../utils/filingApiDates";
import { toast } from "sonner";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { getWorkflowStatus } from "api/applications";

type StampVendorFieldErrors = {
  stampVendorName?: string;
  stampVendorPhone?: string;
};

const validateStampVendorFields = (
  name: string,
  phone: string
): { ok: true } | { ok: false; errors: StampVendorFieldErrors } => {
  const errors: StampVendorFieldErrors = {};
  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  if (!trimmedName) {
    errors.stampVendorName = "Enter the stamp vendor name";
  }
  if (!trimmedPhone) {
    errors.stampVendorPhone = "Enter the stamp vendor phone number";
  } else {
    const digits = trimmedPhone.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      errors.stampVendorPhone = "Enter a valid phone number";
    }
  }
  if (errors.stampVendorName || errors.stampVendorPhone) {
    return { ok: false, errors };
  }
  return { ok: true };
};

const APPLICANT_TYPE_OPTIONS = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  { value: "LIMITED_LIABILITY_PARTNERSHIP", label: "Limited Liability Partnership" },
  { value: "OTHERS", label: "Others (manually enter)" },
];

const STAMP_PAPER_COUNT_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
  value: String(n),
  label: String(n),
}));

type UserDatePerMarkRow = {
  classNumber: number;
  typeOfMark: string;
  userDateType?: string;
  date?: string;
  stampPapersUa?: number;
  stampPapersPoa?: number;
};

const LABEL_STYLE =
  "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

const USER_DATE_PROPOSED = "PROPOSED_TO_BE_USED";

/**
 * Marks with a specific User Date need a User Affidavit, which is executed on UA stamp paper.
 * Returns the keys of such marks that still have 0 UA stamp papers (incomplete).
 */
const collectMissingUaKeys = (rows: UserDatePerMarkRow[]): string[] =>
  rows
    .filter(
      (r) => r.userDateType !== USER_DATE_PROPOSED && (r.stampPapersUa ?? 0) < 1
    )
    .map((r) => `${r.classNumber}::${r.typeOfMark}`);

function buildApplicantAddress(fd: FilingDetailsResponse["filingDetails"]): string {
  const parts = [
    fd.applicantAddressLine,
    fd.applicantAddressDistrict,
    fd.applicantAddressState,
    fd.applicantAddressCountry,
  ].filter(Boolean) as string[];
  return parts.join(", ") || "";
}

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  type = "text",
  inputMode,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={{ ...LABEL_FEATURES, color: "#222222" }}>
        {label}
      </label>
      <div
        className={`${INPUT_BASE} ${
          errorText ? "border-red-400 focus-within:ring-red-400" : ""
        }`}
      >
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`min-w-0 flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
      {errorText ? (
        <p className="text-[11px] leading-4 text-red-600" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

function FetchedDetailsPanel({
  rows,
}: {
  rows: Array<{ label: string; value: string }>;
}) {
  return (
    <div
      className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full"
      style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[200px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              value ? "text-[#222222]" : "text-[#6D6D6D]"
            }`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

type StampPaperDetailsToVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function StampPaperDetailsToVendorView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: StampPaperDetailsToVendorViewProps) {
  const [stampVendorName, setStampVendorName] = useState("");
  const [stampVendorPhone, setStampVendorPhone] = useState("");
  const [stampVendorFieldErrors, setStampVendorFieldErrors] =
    useState<StampVendorFieldErrors>({});
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [applicantTypeOther, setApplicantTypeOther] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [userDatesPerMark, setUserDatesPerMark] = useState<UserDatePerMarkRow[]>([]);
  const [uaErrorKeys, setUaErrorKeys] = useState<Set<string>>(new Set());

  const [stampVendors, setStampVendors] = useState<
    { name: string; phone: string; isDefault?: boolean }[]
  >([]);
  const [fetched, setFetched] = useState<{
    selectedTypesOfMark: string[];
    marks: { classNumber: number; typeOfMark: string; date?: string }[];
  } | null>(null);
  const [isStampAffidavitStepActive, setIsStampAffidavitStepActive] = useState(false);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);

  const [previewAffidavit, setPreviewAffidavit] = useState("");
  const [previewPoa, setPreviewPoa] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const applyFilingResponse = (filingRes: FilingDetailsResponse) => {
    const fd = filingRes.filingDetails;
    setStampVendorName(fd.stampVendorName ?? "");
    setStampVendorPhone(fd.stampVendorPhone ?? "");
    setApplicant(fd.applicant ?? "");
    setApplicantAddress(buildApplicantAddress(fd));
    setApplicantType(fd.applicantType ?? "");
    setApplicantTypeOther(fd.applicantTypeOther ?? "");
    setSignatoryName(fd.signatoryName ?? "");
    setSignatoryDesignation(fd.signatoryDesignation ?? "");
    setStampVendors(filingRes.stampVendors ?? []);
    setIsStampAffidavitStepActive(filingRes.isStampAffidavitStepActive ?? false);
    setFetched(
      filingRes.fetched
        ? {
            selectedTypesOfMark: filingRes.fetched.selectedTypesOfMark ?? [],
            marks: (filingRes.fetched.marks ?? []).map((m) => ({
              ...m,
              date: m.date
                ? normalizeApiDateToIsoForForm(m.date) ?? m.date
                : undefined,
            })),
          }
        : null
    );
    const udpm = fd.userDatesPerMark ?? [];
    const stampPerApp = fd.stampPapersPerApplication ?? [];
    const hasMarks = (filingRes.fetched?.marks?.length ?? 0) > 0;
    const baseRows: UserDatePerMarkRow[] =
      udpm.length > 0
        ? udpm.map((item) => ({
            classNumber: item.classNumber,
            typeOfMark: item.typeOfMark,
            userDateType: item.userDateType,
            date: item.date
              ? normalizeApiDateToIsoForForm(item.date) ?? item.date
              : undefined,
          }))
        : hasMarks
        ? (filingRes.fetched?.marks ?? []).map((m) => {
            const dateIso = m.date
              ? normalizeApiDateToIsoForForm(m.date) ?? m.date
              : undefined;
            return {
              classNumber: m.classNumber,
              typeOfMark: m.typeOfMark,
              userDateType: dateIso ? "SPECIFIC_DATE" : USER_DATE_PROPOSED,
              date: dateIso,
            };
          })
        : [];
    const nextRows = baseRows.map((row) => {
      const spa = stampPerApp.find(
        (s) => s.classNumber === row.classNumber && s.typeOfMark === row.typeOfMark
      );
      return {
        ...row,
        stampPapersUa: spa?.stampPapersUa ?? 0,
        stampPapersPoa: spa?.stampPapersPoa ?? (row.date ? 1 : 0),
      };
    });
    setUserDatesPerMark(nextRows);
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFilingDetails(leadId),
      getStampVendors().catch((): StampVendorOption[] | null => null),
    ])
      .then(([filingRes, vendorsFromApi]) => {
        if (cancelled) return;
        applyFilingResponse(filingRes);
        if (Array.isArray(vendorsFromApi) && vendorsFromApi.length > 0) {
          setStampVendors(vendorsFromApi);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load filing details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const step = status.steps.find(
          (s) => s.slug === "S2_05_STAMP_DETAILS_TO_VENDOR"
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    Promise.all([
      getStampPaperPreview(leadId, "affidavit"),
      getStampPaperPreview(leadId, "poa"),
    ])
      .then(([aff, poa]) => {
        if (!cancelled) {
          setPreviewAffidavit(aff.message ?? "");
          setPreviewPoa(poa.message ?? "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewAffidavit("");
          setPreviewPoa("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleVendorSelect = (value: string): void => {
    if (value === "__none__") {
      return;
    }
    setStampVendorName(value);
    const vendor = stampVendors.find((v) => v.name === value);
    setStampVendorPhone(vendor?.phone ?? "");
    setStampVendorFieldErrors({});
  };

  const vendorSelectValue =
    stampVendorName &&
    stampVendors.some((v) => v.name === stampVendorName)
      ? stampVendorName
      : "__none__";

  const buildStampPaperDetailsToVendorPayload =
    (): UpdateStampPaperDetailsToVendorPayload => ({
      stampPapersPerApplication:
        userDatesPerMark.length > 0
          ? userDatesPerMark.map((row) => ({
              classNumber: row.classNumber,
              typeOfMark: row.typeOfMark,
              stampPapersUa: row.stampPapersUa ?? 0,
              stampPapersPoa: row.stampPapersPoa ?? 0,
            }))
          : undefined,
      stampVendorName: stampVendorName.trim() || undefined,
      stampVendorPhone: stampVendorPhone.trim() || undefined,
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validation = validateStampVendorFields(stampVendorName, stampVendorPhone);
    if ("errors" in validation) {
      setStampVendorFieldErrors(validation.errors);
      const first =
        validation.errors.stampVendorName ?? validation.errors.stampVendorPhone;
      if (first) {
        toast.error(first);
      }
      return;
    }
    setStampVendorFieldErrors({});
    const missingUa = collectMissingUaKeys(userDatesPerMark);
    if (missingUa.length > 0) {
      setUaErrorKeys(new Set(missingUa));
      toast.error(
        "Select at least 1 UA stamp paper for each mark with a User Date."
      );
      return;
    }
    setUaErrorKeys(new Set());
    setSaving(true);
    try {
      await updateStampPaperDetailsToVendor(
        leadId,
        buildStampPaperDetailsToVendorPayload()
      );
      getFilingDetails(leadId).then(applyFilingResponse);
      const [aff, poa] = await Promise.all([
        getStampPaperPreview(leadId, "affidavit"),
        getStampPaperPreview(leadId, "poa"),
      ]);
      setPreviewAffidavit(aff.message ?? "");
      setPreviewPoa(poa.message ?? "");
      if (hasSentWhatsapp) {
        toast.success("Stamp paper details to vendor updated successfully.");
        onSuccess?.(leadId);
      } else {
        setConfirmMode("send");
        setShowConfirmModal(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    if (!previewPoa?.trim()) return;
    setShowConfirmModal(false);
    (async () => {
      try {
        if (confirmMode === "resend") {
          await sendStampPaperWhatsApp(leadId, "poa");
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          toast.success("PoA resent to vendor via WhatsApp.");
        } else if (!hasSentWhatsapp) {
          toast.success("Stamp paper details to vendor saved successfully.");
          await sendStampPaperWhatsApp(leadId, "poa");
          setHasSentWhatsapp(true);
          onSentStatusChange?.(true);
          toast.success("PoA sent to vendor successfully.");
        } else {
          toast.success("Stamp paper details already sent to vendor.");
        }
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      }
    })();
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading filing details...</p>
      </div>
    );
  }

  return (
    <form
      id="stamp-paper-details-to-vendor-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Section: Stamp Paper details to Vendor (Figma layout) */}
      <div className="flex flex-col gap-4 md:gap-5 w-full">
        <h3 className="text-sm font-medium text-[#222222]">Stamp Paper details to Vendor</h3>
        <p className="text-[11px] leading-4 text-[#6D6D6D] -mt-1">
          Choose a saved vendor to autofill name and phone, or type them manually. WhatsApp uses
          the phone number you enter.
        </p>

        {/* Quick select from saved vendors (optional — prefills editable fields) */}
        <div className="flex flex-col w-full gap-1">
          <label className={LABEL_STYLE} style={{ ...LABEL_FEATURES, color: "#222222" }}>
            Quick select vendor (optional)
          </label>
          <Select value={vendorSelectValue} onValueChange={handleVendorSelect}>
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select to autofill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Not from list —</SelectItem>
              {stampVendors
                .filter((v) => typeof v.name === "string" && v.name.trim().length > 0)
                .map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name}
                    {v.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
          <TextField
            label="Name of Stamp Vendor"
            value={stampVendorName}
            onChange={(v) => {
              setStampVendorName(v);
              setStampVendorFieldErrors((prev) => ({
                ...prev,
                stampVendorName: undefined,
              }));
            }}
            placeholder="Vendor name"
            errorText={stampVendorFieldErrors.stampVendorName}
          />
          <TextField
            label="Phone of Stamp Vendor"
            value={stampVendorPhone}
            onChange={(v) => {
              setStampVendorPhone(v);
              setStampVendorFieldErrors((prev) => ({
                ...prev,
                stampVendorPhone: undefined,
              }));
            }}
            placeholder="Phone number"
            type="tel"
            inputMode="tel"
            errorText={stampVendorFieldErrors.stampVendorPhone}
          />
        </div>

        <FetchedDetailsPanel
          rows={[
            {
              label: "Applicant Type",
              value:
                applicantType && applicantType !== "OTHERS"
                  ? APPLICANT_TYPE_OPTIONS.find((o) => o.value === applicantType)?.label ??
                    applicantType
                  : applicantTypeOther,
            },
            { label: "Signatory's Designation", value: signatoryDesignation },
            { label: "Applicant", value: applicant },
            { label: "Applicant Address", value: applicantAddress },
            { label: "Signatory's Name", value: signatoryName },
          ]}
        />

        {/* Classes table: Classes | Type Mark | User Date | Number of Stamp Papers for UA | Number of Stamp Papers for PoA */}
        {userDatesPerMark.length > 0 && (
          <div
            className="flex flex-col w-full"
            style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <div className="hidden lg:grid lg:grid-cols-[160px_230px_170px_215px_225px] gap-5 items-center shrink-0 text-[#222] text-sm font-medium leading-5 mb-4">
              <div>Classes</div>
              <div>Type Mark</div>
              <div>User Date</div>
              <div className="whitespace-nowrap">Number of Stamp Papers for UA</div>
              <div className="whitespace-nowrap">Number of Stamp Papers for PoA</div>
            </div>
            <div className="flex flex-col gap-3">
              {userDatesPerMark.map((item, idx) => (
                <div
                  key={`${item.classNumber}-${item.typeOfMark}-${idx}`}
                  className="grid grid-cols-1 lg:grid-cols-[160px_230px_170px_215px_225px] gap-3 lg:gap-5 lg:items-center w-full p-4 lg:p-0 rounded-[6px] border border-[#EEE] lg:border-0 bg-white lg:bg-transparent"
                >
                  <div className="flex flex-col gap-1">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">Classes</span>
                    <div className="flex w-full items-center justify-between gap-2 min-h-[44px] lg:min-h-0 bg-[#f7f7f7] border border-[#eee] rounded-[6px] p-2.5">
                      <span className="text-[12px] lg:text-sm text-[#222] whitespace-nowrap">
                        Class {item.classNumber}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 shrink-0 text-[#6D6D6D]" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">Type Mark</span>
                    <div className="w-full">
                    {(fetched?.selectedTypesOfMark?.length ?? 0) > 0 ? (
                      <Select
                        value={item.typeOfMark || ""}
                        onValueChange={(v) => {
                          const next = [...userDatesPerMark];
                          next[idx] = { ...item, typeOfMark: v };
                          setUserDatesPerMark(next);
                        }}
                      >
                        <SelectTrigger className="h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            (fetched?.selectedTypesOfMark ?? []).filter((t) => {
                              if (!t || !t.trim()) return false;
                              const usedByOtherRow = userDatesPerMark.some(
                                (r, i) =>
                                  i !== idx &&
                                  r.classNumber === item.classNumber &&
                                  r.typeOfMark === t
                              );
                              return !usedByOtherRow || t === item.typeOfMark;
                            })
                          ).map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs bg-white">
                        <span className={item.typeOfMark ? "text-[#222]" : "text-[#6D6D6D]"}>
                          {item.typeOfMark || "Select an option"}
                        </span>
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">User Date</span>
                    <div className="w-full">
                    {item.userDateType === USER_DATE_PROPOSED ? (
                      <div className="flex items-center h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 bg-white text-[12px] lg:text-xs text-[#222]">
                        Proposed to be used
                      </div>
                    ) : (
                      <DateTimePicker
                        value={item.date ? new Date(item.date) : null}
                        onChange={() => {}}
                        showTime={false}
                        readOnly
                        placeholder="Select Date"
                        buttonClassName="min-h-[44px] lg:min-h-0 text-[12px] lg:text-xs"
                      />
                    )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">Stamp Papers for UA</span>
                    <div className="w-full">
                    {(() => {
                      const uaKey = `${item.classNumber}::${item.typeOfMark}`;
                      const uaHasError = uaErrorKeys.has(uaKey);
                      return (
                        <>
                    <Select
                      value={String(item.stampPapersUa ?? 0)}
                      onValueChange={(v) => {
                        const next = [...userDatesPerMark];
                        next[idx] = { ...item, stampPapersUa: parseInt(v, 10) };
                        setUserDatesPerMark(next);
                        if (uaErrorKeys.has(uaKey)) {
                          setUaErrorKeys((prev) => {
                            const nextKeys = new Set(prev);
                            nextKeys.delete(uaKey);
                            return nextKeys;
                          });
                        }
                      }}
                      disabled={item.userDateType === USER_DATE_PROPOSED}
                    >
                      <SelectTrigger
                        className={`h-9 min-h-[44px] lg:min-h-[36px] w-full border rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D] ${
                          uaHasError ? "border-red-400" : "border-[#eee]"
                        }`}
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {STAMP_PAPER_COUNT_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {uaHasError ? (
                      <p className="text-[11px] leading-4 text-red-600 mt-1" role="alert">
                        Select at least 1 for this mark's User Affidavit.
                      </p>
                    ) : null}
                        </>
                      );
                    })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">Stamp Papers for PoA</span>
                    <div className="w-full">
                    <Select
                      value={String(item.stampPapersPoa ?? 0)}
                      onValueChange={(v) => {
                        const next = [...userDatesPerMark];
                        next[idx] = { ...item, stampPapersPoa: parseInt(v, 10) };
                        setUserDatesPerMark(next);
                      }}
                    >
                      <SelectTrigger className="h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {STAMP_PAPER_COUNT_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={
          isStampAffidavitStepActive
            ? [previewAffidavit || "—", previewPoa || "—"]
            : previewPoa || "—"
        }
        onConfirm={handleConfirmProceed}
      />

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            disabled={resending || saving}
            onClick={async () => {
              if (!leadId || resending || saving) return;
              const validation = validateStampVendorFields(
                stampVendorName,
                stampVendorPhone
              );
              if ("errors" in validation) {
                setStampVendorFieldErrors(validation.errors);
                const first =
                  validation.errors.stampVendorName ??
                  validation.errors.stampVendorPhone;
                if (first) {
                  toast.error(first);
                }
                return;
              }
              setStampVendorFieldErrors({});
              const missingUaResend = collectMissingUaKeys(userDatesPerMark);
              if (missingUaResend.length > 0) {
                setUaErrorKeys(new Set(missingUaResend));
                toast.error(
                  "Select at least 1 UA stamp paper for each mark with a User Date."
                );
                return;
              }
              setUaErrorKeys(new Set());
              setResending(true);
              try {
                await updateStampPaperDetailsToVendor(
                  leadId,
                  buildStampPaperDetailsToVendorPayload()
                );
                const filing = await getFilingDetails(leadId);
                applyFilingResponse(filing);
                const [aff, poa] = await Promise.all([
                  getStampPaperPreview(leadId, "affidavit"),
                  getStampPaperPreview(leadId, "poa"),
                ]);
                setPreviewAffidavit(aff.message ?? "");
                setPreviewPoa(poa.message ?? "");
                setConfirmMode("resend");
                setShowConfirmModal(true);
              } catch (err) {
                toast.error(
                  err instanceof Error ? err.message : "Failed to resend"
                );
              } finally {
                setResending(false);
              }
            }}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}
